import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const API_BASE = 'https://d3bdqxkpn753kf.cloudfront.net';

const Field = ({ label, children }) => (
  <div>
    <label style={{ display: 'block', fontFamily: 'Poppins, sans-serif', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6B7280', marginBottom: '0.2rem' }}>{label}</label>
    {children}
  </div>
);

const CompleteRegistrationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [expired, setExpired] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Validate token on mount
  useEffect(() => {
    if (!token) { setValidating(false); setExpired(true); return; }
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/vrv-validate-registration-token`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });
        const data = await res.json();
        if (res.ok && data.valid) { setTokenValid(true); }
        else { setExpired(true); }
      } catch { setExpired(true); }
      setValidating(false);
    })();
  }, [token]);

  const handleSubmit = async () => {
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setError(null); setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/vrv-signup-complete`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, name, dob, passwordhash: password, confirmPassword })
      });
      const data = await res.json();
      if (res.ok) {
        setShowSuccess(true);
        setTimeout(() => { navigate('/login'); }, 3000);
      } else { setError(data.message || 'Registration failed'); }
    } catch { setError('Error connecting to server'); }
    setLoading(false);
  };

  const inputStyle = { minHeight: '38px', height: '38px', padding: '0 0.75rem', fontSize: '0.82rem' };

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  if (validating) {
    return (
      <div className="h-screen flex items-center justify-center" style={{ background: '#F8F5EE' }}>
        <p style={{ color: '#6B7280', fontFamily: 'Inter, sans-serif' }}>Validating your link...</p>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="h-screen flex items-center justify-center" style={{ background: '#F8F5EE' }}>
        <div className="text-center max-w-sm mx-4">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(185,28,28,0.1)' }}>
            <span className="text-2xl">⏰</span>
          </div>
          <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Poppins, sans-serif', color: '#16213E' }}>Link Expired</h2>
          <p className="text-sm mb-4" style={{ color: '#6B7280', fontFamily: 'Inter, sans-serif' }}>
            This registration link has expired or is invalid. Please sign up again to receive a new link.
          </p>
          <Link to="/signup" className="text-sm font-bold" style={{ color: '#D4AF37' }}>← Back to Sign Up</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex overflow-hidden" style={{ background: '#F8F5EE' }}>
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-5/12 flex-col justify-between p-14 relative overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #0F172A 0%, #16213E 100%)', borderRight: '1px solid rgba(212,175,55,0.12)' }}>
        <div className="absolute inset-0 opacity-[0.025] pointer-events-none flex items-center justify-center select-none" style={{ fontSize: '50vw', fontFamily: '"Cormorant Garamond", serif', color: 'white', overflow: 'hidden' }}>ॐ</div>
        <div className="relative z-10">
          <p className="vrv-tag mb-5">Almost There</p>
          <h2 className="text-5xl font-bold leading-tight mb-6 text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Complete your<br />
            <span style={{ background: 'linear-gradient(135deg, #D4AF37, #B9932F)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>registration</span>
          </h2>
          <div className="vrv-divider mb-6" style={{ width: '60px' }} />
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>
            Set up your profile details and password to join the community.
          </p>
        </div>
        <p className="text-xs italic relative z-10" style={{ color: 'rgba(212,175,55,0.3)', fontFamily: '"Cormorant Garamond", serif' }}>
          "Preserving the sacred tradition for generations"
        </p>
      </div>

      {/* Right Panel */}
      <div className="flex-1 h-full flex flex-col justify-center px-6 sm:px-12 lg:px-14 overflow-hidden">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md mx-auto">
          <p className="vrv-tag mb-0.5" style={{ color: '#D4AF37' }}>Final Step</p>
          <h1 className="text-xl font-bold mb-1" style={{ fontFamily: 'Poppins, sans-serif', color: '#111827' }}>Complete Registration</h1>
          <p className="mb-5" style={{ color: '#6B7280', fontFamily: 'Inter, sans-serif', fontSize: '0.72rem' }}>Fill in your details to finish creating your account</p>

          {tokenValid && (
            <div className="space-y-3">
              <Field label="Full Name *">
                <input type="text" placeholder="Your full name" className="vrv-input" style={inputStyle} value={name} onChange={e => setName(e.target.value)} required autoComplete="new-password" />
              </Field>
              <Field label="Date of Birth *">
                <input type="date" className="vrv-input" style={inputStyle} value={dob} onChange={e => setDob(e.target.value)} required autoComplete="off" />
              </Field>
              <Field label="Password *">
                <input type="password" placeholder="Create a strong password" className="vrv-input" style={inputStyle} value={password} onChange={e => setPassword(e.target.value)} required autoComplete="new-password" />
              </Field>
              <Field label="Confirm Password *">
                <input type="password" placeholder="Re-enter password" className="vrv-input" style={inputStyle} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required autoComplete="new-password" />
              </Field>
              <button onClick={handleSubmit} disabled={loading || !name || !dob || !password || !confirmPassword} className="btn-gold w-full" style={{ height: '40px', fontSize: '0.82rem' }}>
                {loading ? 'Please wait...' : 'Create Account →'}
              </button>
            </div>
          )}

          {error && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
              className="mt-3 px-3 py-2 rounded-xl flex items-start gap-2"
              style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', fontFamily: 'Inter, sans-serif', fontSize: '0.75rem' }}>
              <span>⚠️</span><span>{error}</span>
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)' }}>
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.3 }}
            className="vrv-card p-10 text-center max-w-sm mx-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
              style={{ background: 'linear-gradient(135deg, #D4AF37, #B9932F)' }}>
              <svg className="w-8 h-8" style={{ color: '#0F172A' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Poppins, sans-serif', color: '#16213E' }}>Account Created!</h2>
            <p className="text-sm" style={{ color: '#6B7280', fontFamily: 'Inter, sans-serif' }}>Redirecting to sign in…</p>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default CompleteRegistrationPage;
